import { Link } from 'react-router-dom'
import { useAppSymbols } from '../hooks/useAppSymbols'

interface Props {
  /**
   * Upstream broker order id (or candidate ids — first match wins). Used to
   * resolve the fleet trade group_key. Pass an array if a row has multiple
   * fills that could anchor the link.
   */
  upstreamOrderId: string | null | undefined | Array<string | null | undefined>
  /** Override label when shown. */
  label?: string
  /** Use 'icon' for a tiny ↗ button, 'pill' for a bordered chip. */
  variant?: 'icon' | 'pill'
  /** Stops parent row onClick (e.g. when nested inside a clickable row). */
  stopPropagation?: boolean
  className?: string
  title?: string
}

/**
 * Renders a link to `/trades/:tradeId` if the supplied upstream order id can be
 * mapped to a fleet trade group_key via `useAppSymbols`. Returns null silently
 * when the order wasn't placed through this app (no mapping known) — callers
 * can drop it in unconditionally.
 */
export function TradeLink({
  upstreamOrderId,
  label,
  variant = 'pill',
  stopPropagation,
  className,
  title,
}: Props) {
  const { groupKeyByUpstream } = useAppSymbols()
  const candidates = Array.isArray(upstreamOrderId) ? upstreamOrderId : [upstreamOrderId]
  let groupKey: string | undefined
  for (const id of candidates) {
    if (!id) continue
    const k = groupKeyByUpstream.get(id)
    if (k) {
      groupKey = k
      break
    }
  }
  if (!groupKey) return null
  const to = `/trades/${encodeURIComponent(groupKey)}`
  const onClick = stopPropagation
    ? (e: React.MouseEvent) => e.stopPropagation()
    : undefined
  if (variant === 'icon') {
    return (
      <Link
        to={to}
        onClick={onClick}
        title={title ?? 'View fleet trade detail'}
        className={
          className ??
          'inline-flex items-center justify-center w-5 h-5 rounded text-emerald-400 hover:bg-emerald-950/60 hover:text-emerald-300 text-xs'
        }
      >
        ↗
      </Link>
    )
  }
  return (
    <Link
      to={to}
      onClick={onClick}
      title={title ?? 'View fleet trade detail'}
      className={
        className ??
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-emerald-950/30 hover:bg-emerald-950/60 text-emerald-300 border-emerald-900/60 text-[10px] font-medium uppercase tracking-wide'
      }
    >
      {label ?? 'Trade'} ↗
    </Link>
  )
}
